import { KeyboardEvent, useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './ActionsMenu.module.css';

export interface MenuAction {
  label: string;
  icon: string;
  onSelect: () => void;
  disabled?: boolean;
  danger?: boolean;
  /** Shows a spinning icon while the action runs. */
  busy?: boolean;
  hint?: string;
}

interface ActionsMenuProps {
  /** Falsy entries are left out, so callers can write `cond && { … }`. */
  actions: (MenuAction | false | null | undefined)[];
  /** Accessible name of the button, e.g. "Actions for Alice Smith". */
  label?: string;
}

const GAP = 4;
const MARGIN = 8;

// The menu is portalled to <body> with fixed coordinates: admin tables sit in
// horizontally scrolling wrappers that would clip an absolutely placed menu.
const ActionsMenu = ({ actions, label = 'Actions' }: ActionsMenuProps) => {
  const items = actions.filter(Boolean) as MenuAction[];
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  const close = useCallback((focusTrigger = false) => {
    setOpen(false);
    setPos(null);
    if (focusTrigger) triggerRef.current?.focus();
  }, []);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current || !menuRef.current) return;
    const t = triggerRef.current.getBoundingClientRect();
    const m = menuRef.current.getBoundingClientRect();
    const fitsBelow = t.bottom + GAP + m.height <= window.innerHeight - MARGIN;
    setPos({
      top: fitsBelow ? t.bottom + GAP : Math.max(MARGIN, t.top - GAP - m.height),
      left: Math.max(MARGIN, Math.min(t.right - m.width, window.innerWidth - m.width - MARGIN)),
    });
  }, [open]);

  const enabledItems = () => [...(menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not(:disabled)') ?? [])];

  useEffect(() => {
    if (pos) enabledItems()[0]?.focus({ preventScroll: true });
  }, [pos]);

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!menuRef.current?.contains(target) && !triggerRef.current?.contains(target)) close();
    };
    const onMove = (e: Event) => {
      if (!(e.target instanceof Node && menuRef.current?.contains(e.target))) close();
    };
    document.addEventListener('mousedown', onPointer);
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open, close]);

  const onMenuKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const list = enabledItems();
    const at = list.indexOf(document.activeElement as HTMLButtonElement);
    const move = (i: number) => { e.preventDefault(); list[(i + list.length) % list.length]?.focus(); };
    if (e.key === 'ArrowDown') move(at + 1);
    else if (e.key === 'ArrowUp') move(at - 1);
    else if (e.key === 'Home') move(0);
    else if (e.key === 'End') move(list.length - 1);
    else if (e.key === 'Escape') { e.preventDefault(); close(true); }
    else if (e.key === 'Tab') close();
  };

  if (!items.length) return null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={`${styles.trigger} ${open ? styles.triggerOpen : ''}`}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => (open ? close() : setOpen(true))}
        onKeyDown={(e) => { if (e.key === 'ArrowDown' && !open) { e.preventDefault(); setOpen(true); } }}
      >
        Actions
        <span aria-hidden="true" className="material-symbols-outlined text-base">{open ? 'expand_less' : 'expand_more'}</span>
      </button>
      {open && createPortal(
        <div
          ref={menuRef}
          id={menuId}
          role="menu"
          aria-label={label}
          className={styles.menu}
          style={pos ? { top: pos.top, left: pos.left } : { top: 0, left: 0, visibility: 'hidden' }}
          onKeyDown={onMenuKey}
        >
          {items.map((a) => (
            <button
              key={a.label}
              type="button"
              role="menuitem"
              className={`${styles.item} ${a.danger ? styles.danger : ''}`}
              disabled={a.disabled}
              title={a.hint}
              onClick={() => { close(true); a.onSelect(); }}
            >
              <span aria-hidden="true" className={`material-symbols-outlined text-lg ${a.busy ? 'animate-spin' : ''}`}>{a.busy ? 'refresh' : a.icon}</span>
              {a.label}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </>
  );
};

export default ActionsMenu;
